import React, { ChangeEvent, useCallback, useState } from 'react';

import { Box, RadioButton } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import uuid from 'uuid/v4';

import { ActionButton, ActionButtonVariants } from 'components/action-button';
import { useDeliveryAddressModalContext } from 'components/address-modal/delivery-address-modal.context';
import { ActionStatus } from 'components/address-modal/types';
import { List, ListItem } from 'components/list';
import { useAuthContext } from 'state/auth';

import {
  ContainerOtherLocation,
  ContainerRadioButton,
  PlusIcon,
  StyledAddressLine1,
  StyledAddressLine2,
  StyledModalHeader,
  StyledOtherLocation,
  StyledPostalCode,
  TypesAddressWrapper,
} from '../styled';
import { useSaveDeliveryAddress } from '../use-save-delivery-address';

export const SelectLocation: React.FC<{
  close: VoidFunction;
  onSaveDeliveryAddress: VoidFunction;
}> = ({ close, onSaveDeliveryAddress }) => {
  const { formatMessage } = useIntl();
  const [checkedLocation, setCheckedLocation] = useState('');
  const { dispatchDeliveryAddressState, deliveryAddressLocations, deliveryAddress } =
    useDeliveryAddressModalContext();

  const { user } = useAuthContext();

  const { loading, saveDeliveryAddress } = useSaveDeliveryAddress({
    onClose: close,
    onSaveDeliveryAddress,
    // this value is needed only when enable-saved-addresses-improvements is FALSE
    checkedLocation,
  });

  const onClickSaveDeliveryAddress = useCallback(() => {
    saveDeliveryAddress(deliveryAddress);
  }, [deliveryAddress, saveDeliveryAddress]);

  const onClickRadioButton = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setCheckedLocation(e.currentTarget.value);
  }, []);

  const onClickOtherLocation = useCallback(() => {
    dispatchDeliveryAddressState({
      type: ActionStatus.CONFIRM_SAVE_DELIVERY_ADDRESS_WITH_LOCATION,
    });
  }, [dispatchDeliveryAddressState]);

  const userDeliveryAddressAliases = user?.details?.deliveryAddresses?.map(
    address => address.alias
  );

  // Filter out the delivery location aliases that are already saved by the user
  const filteredDeliveryLocations = deliveryAddressLocations.filter(
    location => !userDeliveryAddressAliases?.includes(location)
  );

  return (
    <>
      <StyledModalHeader id="modal-heading">
        {formatMessage({ id: 'thisAddressCorrespondsTo' })}
      </StyledModalHeader>

      <Box flexDirection="column" margin="2rem 0 0 0">
        <List>
          <ListItem>
            <Box flexDirection="column" margin="1rem 2.75rem">
              <StyledAddressLine1 data-testid="address-line1">
                {`${deliveryAddress?.address.addressLine1}`}
              </StyledAddressLine1>
              <StyledAddressLine2 data-testid="address-line2">
                {`${deliveryAddress?.address.city} - ${deliveryAddress?.address.state}`}
              </StyledAddressLine2>
              <StyledPostalCode data-testid="address-zip">
                {deliveryAddress?.address.zip}
              </StyledPostalCode>
            </Box>
          </ListItem>

          {filteredDeliveryLocations.map(location => (
            <ListItem key={uuid()}>
              <ContainerRadioButton>
                <TypesAddressWrapper>
                  <RadioButton
                    data-testid={`option-${location}`}
                    aria-label={`${formatMessage({ id: 'select' })} ${location}`}
                    checked={location === checkedLocation}
                    value={location}
                    label={location}
                    onChange={onClickRadioButton}
                  />
                </TypesAddressWrapper>
              </ContainerRadioButton>
            </ListItem>
          ))}

          <ListItem>
            <ContainerOtherLocation>
              <PlusIcon />
              <StyledOtherLocation data-testid="other-location" onClick={onClickOtherLocation}>
                {formatMessage({ id: 'otherLocation' })}
              </StyledOtherLocation>
            </ContainerOtherLocation>
          </ListItem>
        </List>

        <Box width="100%" minWidth="auto" minHeight="3rem" margin="2rem 0 1rem 0" center>
          <ActionButton
            fullWidth
            data-testid="btn-save-address"
            aria-label={formatMessage({ id: 'saveAddress' })}
            variant={ActionButtonVariants.PRIMARY}
            onClick={onClickSaveDeliveryAddress}
            isLoading={loading}
            disabled={loading || !checkedLocation}
            eventAttributes={{
              'Address Type': checkedLocation,
            }}
          >
            {formatMessage({ id: 'saveAddress' })}
          </ActionButton>
        </Box>
        <Box width="100%" minWidth="auto" minHeight="3rem" center>
          <ActionButton
            fullWidth
            onClick={close}
            aria-label={formatMessage({ id: 'cancel' })}
            data-testid="btn-cancel"
            variant={ActionButtonVariants.OUTLINE}
          >
            {formatMessage({ id: 'cancel' })}
          </ActionButton>
        </Box>
      </Box>
    </>
  );
};
